import Box from '@material-ui/core/Box';
import useTheme from '@material-ui/core/styles/useTheme';
import { pageUrls } from 'globalconfig/constants/keys';
import { hasEnabledFeature } from 'globalutils/fmUtils';
import PropTypes from 'prop-types';
import React, { lazy, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import Styled from './Clutch';
import Component from './OverrideComponent';
import { mediaUrls } from 'globalconfig/constants/mediaUrls';
import SvgIcon from 'globalcomponents/SvgIcon/Base';

const layout = window?.$environment?.CURRENT_LAYOUT;
const Title = lazy(() => import(`globalcomponents/Title`));
const Breadcrumb = lazy(() => import(`globalcomponents/UI/Breadcrumb/${layout}`));
const DatePopup = lazy(() => import(`@/modules/DatePopup/${layout}`));
const Button = lazy(() => import(`globalcomponents/UI/Button/${layout}`));
const Link = lazy(() => import(`globalcomponents/UI/Link`));

// Breadcrumb Section Component
const BreadcrumbSection = ({ breadcrumbData }) => <Breadcrumb data={breadcrumbData} />;

// Logbook Buttons Component
const LogbookButtons = ({ logBook, i18n, history, setIsBulkDownloadClicked }) => (
  <div className="logbookButtons">
    <div className="logbookTitle">
      <Title header={'logs.logBook'} i18n={i18n} />
    </div>
    <div className="linkContainer commonLinks">
      {hasEnabledFeature('bulkDownloadEnabled') && (
        <Button
          className="buttonBulkDownload"
          type="submit"
          label={
            <>
              <SvgIcon value={mediaUrls.download} />
              {i18n.t('common.bulkDownloadReport')}
            </>
          }
          onClick={() => setIsBulkDownloadClicked(true)}
        />
      )}
      <Link
        className={`uppercase ${logBook?.selectedDailyLog?.status !== 'SIGNED' ? 'logView' : 'logEdit'}`}
        onClick={() => {
          const url =
            logBook?.selectedDailyLog?.status !== 'SIGNED'
              ? pageUrls.logsView.replace(':id', logBook?.selectedDailyLog?.id)
              : pageUrls.logsEdit.replace(':id', logBook?.selectedDailyLog?.idPub);
          history.push(url);
        }}
      >
        {logBook?.selectedDailyLog?.status !== 'SIGNED' ? (
          <>
            <SvgIcon value={mediaUrls.viewMoreIcon} />
            {i18n.t('common.view')}
          </>
        ) : (
          <>
            <SvgIcon value={mediaUrls.logEdit} />
            {i18n.t('common.edit')}
          </>
        )}
      </Link>
      <Link
        className="uppercase history"
        onClick={() => {
          history.push(pageUrls.logsHistory.replace(':id', logBook?.selectedDailyLog?.idPub));
        }}
      >
        <SvgIcon value={mediaUrls.historyLogs} />
        {i18n.t('common.history')}
      </Link>
    </div>
  </div>
);

// Date Popup Section Component
const DatePopupSection = ({
  isBulkDownloadClicked,
  setIsBulkDownloadClicked,
  handleLogBookStatus,
  apiStatus,
  clearApiStatus,
  logBook,
  dailyLogDownloadData,
  formValues,
  callback,
}) => (
  <DatePopup
    formId="bulkDownloadForm"
    isOpen={isBulkDownloadClicked}
    setIsOpen={setIsBulkDownloadClicked}
    exportHandler={handleLogBookStatus}
    apiStatus={apiStatus}
    clearApiStatus={clearApiStatus}
    data={logBook.violationsListDto}
    submit={dailyLogDownloadData}
    params={{ ...formValues }}
    noValidation
    callback={callback}
  />
);

// Main LogBook Component
const LogBook = ({
  history,
  callback,
  apiStatus,
  logBook,
  fetchLogBookView,
  getLogbookEvents,
  updateShowEvents,
  fetchLogbookBreadcrumb,
  handleLogBookStatus,
  clearApiStatus,
  dailyLogDownloadData,
  getDocumentToken,
  drivers,
  dates,
  currentDriver,
  unSignedDates,
  ...rest
}) => {
  const viewApiId = 'fetchLogBookView';
  const fetchLogbookBreadcrumbApiId = 'fetchLogbookBreadcrumb';
  const [isBulkDownloadClicked, setIsBulkDownloadClicked] = useState(false);
  const [formValues, setFormValues] = useState({
    driverId: logBook?.selectedDailyLog?.driverId,
    driverName: logBook?.selectedDailyLog?.driverName,
    type: 'DAILY_LOG',
  });

  useEffect(() => {
    setFormValues({
      driverId: logBook?.selectedDailyLog?.driverId,
      driverName: logBook?.selectedDailyLog?.driverName,
      type: 'DAILY_LOG',
    });
  }, [logBook?.selectedDailyLog?.driverId]);

  const { i18n } = useTranslation();
  const theme = useTheme();
  const match = useParams();
  const breadcrumbData = [
    {
      name: 'Logs',
      url: `${window.$environment.RESELLER || ''}/logs/list`,
    },
    {
      name: 'Manage Logs',
      url: `${window.$environment.RESELLER || ''}/logs/list`,
    },
    {
      name: 'Logbook',
      url: '',
    },
  ];

  return (
    <Styled.LogbookWrap>
      <BreadcrumbSection breadcrumbData={breadcrumbData} />
      <LogbookButtons logBook={logBook} i18n={i18n} history={history} setIsBulkDownloadClicked={setIsBulkDownloadClicked} />
      <DatePopupSection
        isBulkDownloadClicked={isBulkDownloadClicked}
        setIsBulkDownloadClicked={setIsBulkDownloadClicked}
        handleLogBookStatus={handleLogBookStatus}
        apiStatus={apiStatus}
        clearApiStatus={clearApiStatus}
        logBook={logBook}
        dailyLogDownloadData={dailyLogDownloadData}
        formValues={formValues}
        callback={callback}
      />
      <Box style={{ ...theme.palette.contentStyle }}>
        <Component
          {...rest}
          theme={theme}
          i18n={i18n}
          history={history}
          apiStatus={apiStatus}
          viewApiId={viewApiId}
          drivers={drivers}
          dates={dates}
          logBook={logBook}
          currentDriver={currentDriver}
          fetchLogBookView={fetchLogBookView}
          getLogbookEvents={getLogbookEvents}
          updateShowEvents={updateShowEvents}
          fetchLogbookBreadcrumb={fetchLogbookBreadcrumb}
          fetchLogbookBreadcrumbApiId={fetchLogbookBreadcrumbApiId}
          logId={match.id}
          showMap={false}
          Styled={Styled}
          violationListing={true}
          getDocumentToken={getDocumentToken}
          unSignedDates={unSignedDates}
        />
      </Box>
    </Styled.LogbookWrap>
  );
};

export default LogBook;

// PropTypes Validation
LogBook.propTypes = {
  history: PropTypes.object.isRequired,
  apiStatus: PropTypes.object.isRequired,
  callback: PropTypes.func.isRequired,
  logBook: PropTypes.object.isRequired,
  fetchLogBookView: PropTypes.func.isRequired,
  getLogbookEvents: PropTypes.func.isRequired,
  updateShowEvents: PropTypes.func.isRequired,
  handleLogBookStatus: PropTypes.func.isRequired,
  clearApiStatus: PropTypes.func.isRequired,
  fetchLogbookBreadcrumb: PropTypes.func.isRequired,
  dailyLogDownloadData: PropTypes.func.isRequired,
  drivers: PropTypes.object.isRequired,
  dates: PropTypes.array.isRequired,
  currentDriver: PropTypes.object.isRequired,
  unSignedDates: PropTypes.array.isRequired,
};
